import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtUSD, fmtPct, fmtHold } from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/Bits";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ReferenceLine } from "recharts";
import { ChevronDown, ChevronRight } from "lucide-react";
import PeriodTabs from "@/components/PeriodTabs";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[color:var(--bg)] border border-[color:var(--border)] px-3 py-2 text-xs font-mono-num pointer-events-none shadow-lg">
      <div className="text-[color:var(--fg-3)]">{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || "#34d399" }}>{p.name}: {typeof p.value === "number" ? p.value.toFixed(2) : p.value}</div>
      ))}
    </div>
  );
};

export default function Analytics() {
  const [showSymbols, setShowSymbols] = useState(false);
  const [period, setPeriod] = useState("all");
  // When the user picks a specific date range via the calendar popover, we
  // switch `period` to "custom" and drive queries off `customFrom` / `customTo`
  // instead. Every card on the page (including the new float/RVOL/mktcap
  // enrichment section) reads from these same three variables.
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const rangeParams = period === "custom"
    ? { from: customFrom, to: customTo }
    : { period };

  const rangeKey = period === "custom" ? `custom:${customFrom}:${customTo}` : period;

  const rangeControl = (
    <PeriodTabs
      value={period}
      onChange={setPeriod}
      customFrom={customFrom}
      customTo={customTo}
      onCustomChange={({ from, to }) => {
        setCustomFrom(from);
        setCustomTo(to);
        if (from && to) setPeriod("custom");
      }}
    />
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["stats", rangeKey],
    queryFn: async () => (await api.get("/stats/overview", { params: rangeParams })).data,
    enabled: period !== "custom" || (!!customFrom && !!customTo),
    retry: 2,
  });

  const { data: enrichment } = useQuery({
    queryKey: ["stats-enrichment", rangeKey],
    queryFn: async () => (await api.get("/stats/enrichment", { params: rangeParams })).data,
    enabled: period !== "custom" || (!!customFrom && !!customTo),
    staleTime: 60_000,
  });

  if (isLoading) return <div className="p-8 text-xs font-mono-num text-[color:var(--fg-3)]">LOADING…</div>;
  // Distinguish "API errored" from "0 trades in range" — otherwise a 500
  // response looks identical to a genuinely empty range, which sent multiple
  // customers hunting for phantom missing data.
  if (isError) {
    const detail = error?.response?.data?.detail || error?.message || "Unknown error";
    const status = error?.response?.status;
    return (
      <div>
        <PageHeader eyebrow="ANALYTICS" title="Performance Breakdowns" action={rangeControl} />
        <div className="p-8">
          <Empty
            title="Analytics couldn't load."
            sub={`The stats endpoint returned an error${status ? ` (HTTP ${status})` : ""}. Try refreshing — if it persists, this is a bug. Details: ${detail}`}
          />
        </div>
      </div>
    );
  }
  if (!data || data.total_trades === 0) {
    return (
      <div>
        <PageHeader eyebrow="ANALYTICS" title="Performance Breakdowns"
          action={rangeControl} />
        <div className="p-8"><Empty title="Not enough data in this range." sub="Try widening the range or log more trades." /></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader eyebrow="ANALYTICS" title="Performance Breakdowns"
        action={rangeControl} />

      {/* Top-line summary tiles: the two numbers that answer "am I making
          money?" at a glance. Sits right under the page title so users don't
          have to scroll to find the headline metrics. */}
      <div className="px-8 pt-2">
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[color:var(--border)] [&>*]:border-0 [&>*]:border-r [&>*]:border-[color:var(--border)] [&>*:last-child]:border-r-0"
          data-testid="analytics-summary"
        >
          <div className="p-5">
            <div className="label-eyebrow text-[color:var(--fg-3)]">NET P&L</div>
            <div
              className={`font-mono-num text-3xl mt-2 ${data.total_pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}
              data-testid="analytics-net-pnl"
            >
              {fmtUSD(data.total_pnl)}
            </div>
            <div className="text-[10px] text-[color:var(--fg-3)] mt-1 font-mono-num">
              {data.total_trades} TRADES
            </div>
          </div>
          <div className="p-5">
            <div className="label-eyebrow text-[color:var(--fg-3)]">WIN RATE</div>
            <div className="font-mono-num text-3xl mt-2 text-[color:var(--fg)]" data-testid="analytics-win-rate">
              {fmtPct(data.win_rate)}
            </div>
            <div className="text-[10px] text-[color:var(--fg-3)] mt-1 font-mono-num">
              PROFIT FACTOR {(data.profit_factor || 0).toFixed(2)}
            </div>
          </div>
          <div className="p-5">
            <div className="label-eyebrow text-[color:var(--fg-3)]">EXPECTANCY</div>
            <div
              className={`font-mono-num text-3xl mt-2 ${data.expectancy >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}
              data-testid="analytics-expectancy"
            >
              {fmtUSD(data.expectancy)}
            </div>
            <div className="text-[10px] text-[color:var(--fg-3)] mt-1 font-mono-num">
              PER TRADE
            </div>
          </div>
          <div className="p-5">
            <div className="label-eyebrow text-[color:var(--fg-3)]">MAX DRAWDOWN</div>
            <div className="font-mono-num text-3xl mt-2 text-rose-400" data-testid="analytics-max-dd">
              {fmtUSD(data.max_drawdown)}
            </div>
            <div className="text-[10px] text-[color:var(--fg-3)] mt-1 font-mono-num">
              SHARPE {(data.sharpe || 0).toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-6">

        {/* Note: Hold Times + Execution used to be their own cards here. They
            now live as rows inside the "METRICS · The numbers that matter"
            card below so all headline stats are consolidated in one place. */}

        <Card eyebrow="METRICS" title="The numbers that matter">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[color:var(--border)] [&>*]:border-0 [&>*]:border-r [&>*]:border-b [&>*]:border-[color:var(--border)] [&>*:nth-child(4n)]:border-r-0">
            {/* Row 1 — execution quality (avg win, avg loss, R:R, expectancy) */}
            <div className="p-4">
              <div className="label-eyebrow text-[color:var(--green)]">AVG WIN</div>
              <div className="font-mono-num text-xl mt-1 text-[color:var(--green)]" data-testid="metric-avg-win">{fmtUSD(data.avg_win)}</div>
              <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5 font-mono-num">{(data.avg_win_cents_per_share || 0).toFixed(1)}¢/SHARE</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow text-rose-500">AVG LOSS</div>
              <div className="font-mono-num text-xl mt-1 text-rose-400" data-testid="metric-avg-loss">{fmtUSD(data.avg_loss)}</div>
              <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5 font-mono-num">{(data.avg_loss_cents_per_share || 0).toFixed(1)}¢/SHARE</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow text-[color:var(--accent)]">RISK:REWARD</div>
              <div className="font-mono-num text-xl mt-1 text-[color:var(--fg)]" data-testid="metric-rr">{(data.rr_ratio || 0).toFixed(2)}:1</div>
              <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5 font-mono-num">EXPECTANCY {fmtUSD(data.expectancy)}</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow">PROFIT FACTOR</div>
              <div className="font-mono-num text-xl mt-1 text-[color:var(--fg)]" data-testid="metric-pf">{(data.profit_factor || 0).toFixed(2)}</div>
              <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5 font-mono-num">SHARPE {(data.sharpe || 0).toFixed(2)}</div>
            </div>

            {/* Row 2 — hold times (how long you hang on to winners vs losers) */}
            <div className="p-4">
              <div className="label-eyebrow text-[color:var(--green)]">AVG WIN HOLD</div>
              <div className="font-mono-num text-xl mt-1 text-[color:var(--green)]" data-testid="metric-avg-win-hold">{fmtHold(data.avg_win_hold)}</div>
              <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5">How long you let winners run.</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow text-rose-500">AVG LOSS HOLD</div>
              <div className="font-mono-num text-xl mt-1 text-rose-400" data-testid="metric-avg-loss-hold">{fmtHold(data.avg_loss_hold)}</div>
              <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5">How long you sit in losers.</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow">HOLD RATIO</div>
              <div className="font-mono-num text-xl mt-1 text-[color:var(--fg)]" data-testid="metric-hold-ratio">
                {(data.avg_win_hold > 0 && data.avg_loss_hold > 0)
                  ? `${(data.avg_win_hold / data.avg_loss_hold).toFixed(2)}×`
                  : "—"}
              </div>
              <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5">
                {(data.avg_win_hold > 0 && data.avg_loss_hold > 0 && data.avg_win_hold >= data.avg_loss_hold)
                  ? "Riding winners"
                  : (data.avg_win_hold > 0 && data.avg_loss_hold > 0)
                  ? "Sitting in losers"
                  : "—"}
              </div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow">AVG TRADES/DAY</div>
              <div className="font-mono-num text-xl mt-1 text-[color:var(--fg)]" data-testid="metric-tpd">{data.avg_trades_per_day}</div>
              <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5">Session volume</div>
            </div>

            {/* Row 3 — streaks + best/worst days */}
            <div className="p-4">
              <div className="label-eyebrow">CURRENT STREAK</div>
              <div className={`font-mono-num text-xl mt-1 ${data.current_streak > 0 ? "text-[color:var(--green)]" : data.current_streak < 0 ? "text-rose-400" : "text-[color:var(--fg-2)]"}`} data-testid="metric-current-streak">
                {data.current_streak > 0 ? `+${data.current_streak}W` : data.current_streak < 0 ? `${Math.abs(data.current_streak)}L` : "—"}
              </div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow">LONGEST WIN STREAK</div>
              <div className="font-mono-num text-xl mt-1 text-[color:var(--green)]" data-testid="metric-longest-win">{data.longest_win_streak}W</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow">LONGEST LOSS STREAK</div>
              <div className="font-mono-num text-xl mt-1 text-rose-400" data-testid="metric-longest-loss">{data.longest_loss_streak}L</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow">AVG POSITION</div>
              <div className="font-mono-num text-xl mt-1 text-[color:var(--fg)]">{fmtUSD(data.avg_position_dollars)}</div>
              <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5">{data.avg_shares?.toLocaleString()} sh avg</div>
            </div>

            {/* Row 4 — best/worst day + fees */}
            <div className="p-4">
              <div className="label-eyebrow">BEST DAY</div>
              <div className="font-mono-num text-xl mt-1 text-[color:var(--green)]">{fmtUSD(data.best_day_pnl)}</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow">WORST DAY</div>
              <div className="font-mono-num text-xl mt-1 text-rose-400">{fmtUSD(data.worst_day_pnl)}</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow text-rose-500">FEES PAID</div>
              <div className="font-mono-num text-xl mt-1 text-rose-400" data-testid="metric-fees">{fmtUSD(data.total_fees)}</div>
              <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5">GROSS {fmtUSD(data.gross_pnl)}</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow">MAX DRAWDOWN</div>
              <div className="font-mono-num text-xl mt-1 text-rose-400" data-testid="metric-dd">{fmtUSD(data.max_drawdown)}</div>
              <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5">Peak-to-trough</div>
            </div>
          </div>
        </Card>

        <Card eyebrow="BY PRICE TIER" title="How you do across stock prices">
          {!data.by_price_tier?.length ? (
            <div className="text-xs text-[color:var(--fg-3)] p-3">Need trades with entry prices to compute.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono-num">
                <thead>
                  <tr className="border-b border-[color:var(--border)] text-[color:var(--fg-3)] label-eyebrow">
                    <th className="text-left p-3">TIER</th>
                    <th className="text-right p-3">TRADES</th>
                    <th className="text-right p-3">WIN RATE</th>
                    <th className="text-right p-3">AVG P&L</th>
                    <th className="text-right p-3">TOTAL P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_price_tier.map((b, i) => (
                    <tr key={b.tier} className="border-b border-[color:var(--border)] hover:bg-[color:var(--surface)]" data-testid={`price-tier-${i}`}>
                      <td className="p-3 text-[color:var(--fg)] font-semibold">{b.tier}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{b.trades}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{fmtPct(b.win_rate)}</td>
                      <td className={`p-3 text-right ${b.avg_pnl >= 0 ? "text-[color:var(--green)]/90" : "text-[color:var(--loss)]/90"}`}>{fmtUSD(b.avg_pnl)}</td>
                      <td className={`p-3 text-right ${b.pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(b.pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card eyebrow="BY POSITION SIZE" title="How you do at different size buckets">
          {!data.by_volume?.length ? (
            <div className="text-xs text-[color:var(--fg-3)] p-3">Need trades with entry prices to compute.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono-num">
                <thead>
                  <tr className="border-b border-[color:var(--border)] text-[color:var(--fg-3)] label-eyebrow">
                    <th className="text-left p-3">$ POSITION</th>
                    <th className="text-right p-3">TRADES</th>
                    <th className="text-right p-3">WIN RATE</th>
                    <th className="text-right p-3">AVG P&L</th>
                    <th className="text-right p-3">TOTAL P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_volume.map((b, i) => (
                    <tr key={b.bucket} className="border-b border-[color:var(--border)] hover:bg-[color:var(--surface)]" data-testid={`volume-bucket-${i}`}>
                      <td className="p-3 text-[color:var(--fg)] font-semibold">{b.bucket}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{b.trades}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{fmtPct(b.win_rate)}</td>
                      <td className={`p-3 text-right ${b.avg_pnl >= 0 ? "text-[color:var(--green)]/90" : "text-[color:var(--loss)]/90"}`}>{fmtUSD(b.avg_pnl)}</td>
                      <td className={`p-3 text-right ${b.pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(b.pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Ticker enrichment — Float / RVOL / Market Cap breakdown from FMP.
            All three follow the global range control at the top of the page. */}
        <EnrichmentSection enrichment={enrichment} />


        <Card eyebrow="BY HOLD DURATION" title="How long you hold winners vs losers">
          {!data.by_duration?.length ? (
            <div className="text-xs text-[color:var(--fg-3)] p-3">No data.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono-num">
                <thead>
                  <tr className="border-b border-[color:var(--border)] text-[color:var(--fg-3)] label-eyebrow">
                    <th className="text-left p-3">DURATION</th>
                    <th className="text-right p-3">TRADES</th>
                    <th className="text-right p-3">WIN RATE</th>
                    <th className="text-right p-3">TOTAL P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_duration.map((d) => (
                    <tr key={d.bucket} className="border-b border-[color:var(--border)] last:border-0 hover:bg-[color:var(--surface)]" data-testid={`dur-${d.bucket}`}>
                      <td className="p-3 text-[color:var(--fg)] font-semibold">{d.bucket}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{d.trades}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{fmtPct(d.win_rate)}</td>
                      <td className={`p-3 text-right ${d.pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(d.pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Small-cap day-trader specific metrics */}
        <Card eyebrow="TIME-OF-DAY WINDOWS" title="When you actually make money — small-cap session map">
          {!data.by_time_window?.length ? (
            <div className="text-xs text-[color:var(--fg-3)] p-3">Need trades with entry timestamps.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono-num">
                <thead>
                  <tr className="border-b border-[color:var(--border)] text-[color:var(--fg-3)] label-eyebrow">
                    <th className="text-left p-3">SESSION WINDOW</th>
                    <th className="text-right p-3">TRADES</th>
                    <th className="text-right p-3">WIN RATE</th>
                    <th className="text-right p-3">AVG P&L</th>
                    <th className="text-right p-3">TOTAL P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_time_window.map((w) => (
                    <tr key={w.window} className="border-b border-[color:var(--border)] last:border-0 hover:bg-[color:var(--surface)]" data-testid={`tod-${w.window}`}>
                      <td className="p-3 text-[color:var(--fg)] font-semibold">{w.window}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{w.trades}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{fmtPct(w.win_rate)}</td>
                      <td className={`p-3 text-right ${w.avg_pnl >= 0 ? "text-[color:var(--green)]/90" : "text-[color:var(--loss)]/90"}`}>{fmtUSD(w.avg_pnl)}</td>
                      <td className={`p-3 text-right ${w.pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(w.pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card eyebrow="OPENING DRIVES" title="Premarket · Cash Open · Rest of session">
          {data.open_drive_stats ? (
            <div className="grid grid-cols-3 gap-0 border border-[color:var(--border)] [&>*]:border-0 [&>*]:border-r [&>*]:border-[color:var(--border)] [&>*:last-child]:border-r-0">
              <div className="p-5" data-testid="premarket-drive-stats">
                <div className="label-eyebrow text-sky-400">4:00 — 9:29 ET</div>
                <div className={`font-mono-num text-2xl mt-2 ${data.open_drive_stats.premarket_drive_pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(data.open_drive_stats.premarket_drive_pnl)}</div>
                <div className="text-[11px] text-[color:var(--fg-3)] mt-1 font-mono-num">{data.open_drive_stats.premarket_drive_trades} TRADES · {fmtPct(data.open_drive_stats.premarket_drive_win_rate)} WR</div>
                <div className="text-[10px] text-[color:var(--fg-3)] mt-1">PREMARKET</div>
              </div>
              <div className="p-5" data-testid="open-drive-stats">
                <div className="label-eyebrow text-amber-500">9:30 — 9:44 ET</div>
                <div className={`font-mono-num text-2xl mt-2 ${data.open_drive_stats.open_drive_pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(data.open_drive_stats.open_drive_pnl)}</div>
                <div className="text-[11px] text-[color:var(--fg-3)] mt-1 font-mono-num">{data.open_drive_stats.open_drive_trades} TRADES · {fmtPct(data.open_drive_stats.open_drive_win_rate)} WR</div>
                <div className="text-[10px] text-[color:var(--fg-3)] mt-1">CASH OPEN DRIVE</div>
              </div>
              <div className="p-5" data-testid="rest-day-stats">
                <div className="label-eyebrow text-[color:var(--fg-2)]">9:45 ET ONWARDS</div>
                <div className={`font-mono-num text-2xl mt-2 ${data.open_drive_stats.rest_of_day_pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(data.open_drive_stats.rest_of_day_pnl)}</div>
                <div className="text-[11px] text-[color:var(--fg-3)] mt-1 font-mono-num">{data.open_drive_stats.rest_of_day_trades} TRADES · {fmtPct(data.open_drive_stats.rest_of_day_win_rate)} WR</div>
                <div className="text-[10px] text-[color:var(--fg-3)] mt-1">REST OF SESSION</div>
              </div>
            </div>
          ) : <div className="text-xs text-[color:var(--fg-3)] p-3">No data.</div>}
          <div className="mt-3 px-2 text-[11px] font-mono-num text-[color:var(--fg-3)]">
            Full premarket session (4:00–9:29 ET) vs the cash-open drive (9:30–9:44 ET) vs the rest of the day. All times in New York (auto DST-adjusted).
          </div>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card eyebrow="FIRST TRADE IMPACT" title="Does the first trade set the day's tone?">
            {data.first_trade_impact ? (
              <div className="grid grid-cols-2 gap-0 border border-[color:var(--border)] [&>*]:border-0 [&>*]:border-r [&>*]:border-[color:var(--border)] [&>*:last-child]:border-r-0">
                <div className="p-5" data-testid="first-green-stats">
                  <div className="label-eyebrow text-[color:var(--green)]">FIRST TRADE GREEN</div>
                  <div className={`font-mono-num text-2xl mt-2 ${data.first_trade_impact.avg_day_after_green_first >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(data.first_trade_impact.avg_day_after_green_first)}</div>
                  <div className="text-[11px] text-[color:var(--fg-3)] mt-1 font-mono-num">{data.first_trade_impact.first_green_count} DAYS · {fmtPct(data.first_trade_impact.green_first_win_rate)} GREEN-DAY RATE</div>
                </div>
                <div className="p-5" data-testid="first-red-stats">
                  <div className="label-eyebrow text-rose-500">FIRST TRADE RED</div>
                  <div className={`font-mono-num text-2xl mt-2 ${data.first_trade_impact.avg_day_after_red_first >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(data.first_trade_impact.avg_day_after_red_first)}</div>
                  <div className="text-[11px] text-[color:var(--fg-3)] mt-1 font-mono-num">{data.first_trade_impact.first_red_count} DAYS · {fmtPct(data.first_trade_impact.red_first_win_rate)} GREEN-DAY RATE</div>
                </div>
              </div>
            ) : <div className="text-xs text-[color:var(--fg-3)] p-3">No data.</div>}
            <div className="mt-3 px-2 text-[11px] font-mono-num text-[color:var(--fg-3)]">
              Average day P&L when the first trade was a winner vs a loser. If red-first days are much worse, your tilt response needs work.
            </div>
          </Card>

          <Card eyebrow="REVENGE-TRADE DETECTOR" title="Performance after streaks">
            {data.consecutive_impact ? (
              <div className="grid grid-cols-3 gap-0 border border-[color:var(--border)] [&>*]:border-0 [&>*]:border-r [&>*]:border-[color:var(--border)] [&>*:last-child]:border-r-0">
                <div className="p-5" data-testid="after-2w">
                  <div className="label-eyebrow text-[color:var(--green)]">AFTER 2W</div>
                  <div className={`font-mono-num text-xl mt-2 ${data.consecutive_impact.after_2_wins_avg >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(data.consecutive_impact.after_2_wins_avg)}</div>
                  <div className="text-[10px] text-[color:var(--fg-3)] mt-1 font-mono-num">N={data.consecutive_impact.after_2_wins_count}</div>
                </div>
                <div className="p-5" data-testid="after-2l">
                  <div className="label-eyebrow text-rose-500">AFTER 2L</div>
                  <div className={`font-mono-num text-xl mt-2 ${data.consecutive_impact.after_2_losses_avg >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(data.consecutive_impact.after_2_losses_avg)}</div>
                  <div className="text-[10px] text-[color:var(--fg-3)] mt-1 font-mono-num">N={data.consecutive_impact.after_2_losses_count}</div>
                </div>
                <div className="p-5" data-testid="after-3l">
                  <div className="label-eyebrow text-rose-600">AFTER 3L</div>
                  <div className={`font-mono-num text-xl mt-2 ${data.consecutive_impact.after_3_losses_avg >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(data.consecutive_impact.after_3_losses_avg)}</div>
                  <div className="text-[10px] text-[color:var(--fg-3)] mt-1 font-mono-num">N={data.consecutive_impact.after_3_losses_count}</div>
                </div>
              </div>
            ) : <div className="text-xs text-[color:var(--fg-3)] p-3">No data.</div>}
            <div className="mt-3 px-2 text-[11px] font-mono-num text-[color:var(--fg-3)]">
              Avg next-trade P&L after streaks. Red &quot;after losses&quot; = tilt — step away.
            </div>
          </Card>
        </div>


        <Card eyebrow="BY SETUP / TAG" title="Which playbooks actually work">
          {!data.by_setup?.length ? (
            <div className="text-xs text-[color:var(--fg-3)] p-3">Tag your trades with a setup to see this breakdown.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono-num">
                <thead>
                  <tr className="border-b border-[color:var(--border)] text-[color:var(--fg-3)] label-eyebrow">
                    <th className="text-left p-3">SETUP</th>
                    <th className="text-right p-3">TRADES</th>
                    <th className="text-right p-3">WIN RATE</th>
                    <th className="text-right p-3">AVG P&L</th>
                    <th className="text-right p-3">TOTAL P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_setup.map((s) => (
                    <tr key={s.setup} className="border-b border-[color:var(--border)] hover:bg-[color:var(--surface)]" data-testid={`setup-row-${s.setup}`}>
                      <td className="p-3 text-[color:var(--fg)] font-semibold">{s.setup}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{s.trades}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{fmtPct(s.win_rate)}</td>
                      <td className={`p-3 text-right ${s.avg_pnl >= 0 ? "text-[color:var(--green)]/90" : "text-[color:var(--loss)]/90"}`}>{fmtUSD(s.avg_pnl)}</td>
                      <td className={`p-3 text-right ${s.pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(s.pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card eyebrow="WEEKLY P&L" title="Performance by ISO week">
            {!data.weekly_pnl?.length ? (
              <div className="text-xs text-[color:var(--fg-3)] p-3">No data.</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.weekly_pnl}>
                    <XAxis dataKey="week" stroke="#52525b" fontSize={10} tick={{ fontFamily: "JetBrains Mono" }} />
                    <YAxis stroke="#52525b" fontSize={10} tick={{ fontFamily: "JetBrains Mono" }} />
                    <Tooltip content={ChartTooltip} cursor={{ fill: "rgba(56,189,248,0.08)" }} isAnimationActive={false} />
                    <ReferenceLine y={0} stroke="#3f3f46" />
                    <Bar dataKey="pnl">
                      {data.weekly_pnl.map((d) => (
                        <Cell key={d.week} fill={d.pnl >= 0 ? "#34d399" : "#fb7185"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card eyebrow="MONTHLY P&L" title="Performance by calendar month">
            {!data.monthly_pnl?.length ? (
              <div className="text-xs text-[color:var(--fg-3)] p-3">No data.</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly_pnl}>
                    <XAxis dataKey="month" stroke="#52525b" fontSize={10} tick={{ fontFamily: "JetBrains Mono" }} />
                    <YAxis stroke="#52525b" fontSize={10} tick={{ fontFamily: "JetBrains Mono" }} />
                    <Tooltip content={ChartTooltip} cursor={{ fill: "rgba(56,189,248,0.08)" }} isAnimationActive={false} />
                    <ReferenceLine y={0} stroke="#3f3f46" />
                    <Bar dataKey="pnl">
                      {data.monthly_pnl.map((d) => (
                        <Cell key={d.month} fill={d.pnl >= 0 ? "#34d399" : "#fb7185"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card eyebrow="BY DAY OF WEEK" title="When you make money">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.by_day_of_week}>
                  <XAxis dataKey="day" stroke="#52525b" fontSize={10} tick={{ fontFamily: "JetBrains Mono" }} />
                  <YAxis stroke="#52525b" fontSize={10} tick={{ fontFamily: "JetBrains Mono" }} />
                  <Tooltip content={ChartTooltip} cursor={{ fill: "rgba(56,189,248,0.08)" }} isAnimationActive={false} />
                  <ReferenceLine y={0} stroke="#3f3f46" />
                  <Bar dataKey="pnl">
                    {data.by_day_of_week.map((d) => (
                      <Cell key={d.day} fill={d.pnl >= 0 ? "#34d399" : "#fb7185"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card eyebrow="BY HOUR" title="Time-of-day performance">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.by_hour}>
                  <XAxis dataKey="hour" stroke="#52525b" fontSize={10} tick={{ fontFamily: "JetBrains Mono" }} />
                  <YAxis stroke="#52525b" fontSize={10} tick={{ fontFamily: "JetBrains Mono" }} />
                  <Tooltip content={ChartTooltip} cursor={{ fill: "rgba(56,189,248,0.08)" }} isAnimationActive={false} />
                  <ReferenceLine y={0} stroke="#3f3f46" />
                  <Bar dataKey="pnl">
                    {data.by_hour.map((d) => (
                      <Cell key={d.hour} fill={d.pnl >= 0 ? "#34d399" : "#fb7185"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* BY SYMBOL — collapsible, at the bottom */}
        <div className="border border-[color:var(--border)]">
          <button
            type="button"
            onClick={() => setShowSymbols(s => !s)}
            data-testid="toggle-by-symbol"
            className="w-full flex items-center justify-between p-4 hover:bg-[color:var(--surface)] transition-colors text-left"
          >
            <div>
              <div className="label-eyebrow text-[color:var(--fg-3)]">BY SYMBOL</div>
              <div className="text-sm text-[color:var(--fg)] mt-0.5">P&L per instrument · {data.by_symbol?.length || 0} symbol{data.by_symbol?.length === 1 ? "" : "s"}</div>
            </div>
            {showSymbols ? <ChevronDown className="h-4 w-4 text-[color:var(--fg-3)]" /> : <ChevronRight className="h-4 w-4 text-[color:var(--fg-3)]" />}
          </button>
          {showSymbols && (
            <div className="border-t border-[color:var(--border)] overflow-x-auto">
              <table className="w-full text-xs font-mono-num">
                <thead>
                  <tr className="border-b border-[color:var(--border)] text-[color:var(--fg-3)] label-eyebrow">
                    <th className="text-left p-3">SYMBOL</th>
                    <th className="text-right p-3">AVG PRICE</th>
                    <th className="text-right p-3">TRADES</th>
                    <th className="text-right p-3">VOLUME</th>
                    <th className="text-right p-3">WIN RATE</th>
                    <th className="text-right p-3">P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_symbol.map((s) => (
                    <tr key={s.symbol} className="border-b border-[color:var(--border)] hover:bg-[color:var(--surface)]" data-testid={`symbol-row-${s.symbol}`}>
                      <td className="p-3 text-[color:var(--fg)] font-semibold">{s.symbol}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">${(s.avg_price || 0).toFixed(s.avg_price < 1 ? 4 : 2)}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{s.trades}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{(s.volume || 0).toLocaleString()}</td>
                      <td className="p-3 text-right text-[color:var(--fg-2)]">{fmtPct(s.win_rate)}</td>
                      <td className={`p-3 text-right ${s.pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(s.pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Ticker-enrichment section — three sibling cards driven by /stats/enrichment.
//   • Float Analysis (nano → large by shares outstanding)
//   • Volume / RVOL (relative volume on trade day vs 30d average)
//   • Market Cap (nano-cap → mega-cap)
// All three follow the global date range picked at the top of the Analytics
// page — no local date state.
// ---------------------------------------------------------------------------
function EnrichmentSection({ enrichment }) {
  return (
    <div className="space-y-6" data-testid="enrichment-section">
      <Card eyebrow="FLOAT ANALYSIS" title="How you do by float size — the day-trader edge finder">
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="text-[11px] text-[color:var(--fg-3)]">Micro-floats (&lt;10M) squeeze hardest. Where&apos;s YOUR edge?</div>
          <div className="text-[10px] text-[color:var(--fg-3)] whitespace-nowrap ml-4">
            {typeof enrichment?.float_coverage === "number" ? `${enrichment.float_coverage}% of trades have float data` : ""}
            {enrichment?.fmp_configured === false && " · admin: set FMP_API_KEY to enable"}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border)] [&>*]:border-0 [&>*]:border-r [&>*]:border-b [&>*]:border-[color:var(--border)] md:[&>*:last-child]:border-r-0 [&>*:nth-child(5n)]:border-r-0 md:[&>*]:border-b-0">
          {(enrichment?.float_buckets || []).filter(b => b.bucket !== "unknown").map((b) => {
            const isPositive = b.total_pnl >= 0;
            const hasData = b.trades > 0;
            return (
              <div key={b.bucket} className="p-5" data-testid={`float-row-${b.bucket}`}>
                <div className="label-eyebrow text-[color:var(--fg-3)]">{b.bucket}</div>
                <div className={`font-mono-num text-2xl mt-2 ${!hasData ? "text-[color:var(--fg-3)]" : isPositive ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>
                  {hasData ? fmtUSD(b.total_pnl) : "—"}
                </div>
                <div className="text-[11px] text-[color:var(--fg-3)] mt-1 font-mono-num">
                  {b.trades} TRADES · {b.win_rate}% WR
                </div>
                <div className="text-[10px] text-[color:var(--fg-3)] mt-0.5 font-mono-num">
                  AVG {hasData ? fmtUSD(b.avg_pnl) : "—"}
                </div>
                {hasData && b.symbols?.length > 0 && (
                  <div className="text-[10px] text-[color:var(--fg-3)]/70 mt-2 truncate" title={b.symbols.join(", ")}>
                    {b.symbols.slice(0, 3).join(", ")}{b.symbols.length > 3 && "…"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
      {/* NOTE: The VOLUME · RVOL bucket card used to live here. Removed until
          our data provider supports the micro-cap tickers our users trade —
          all rows were showing $0.00 because FMP's basic plan returns 402 on
          those symbols. Backend still computes and exposes `rvol_buckets` via
          /stats/enrichment, so re-adding the card is a 1-line JSX restore. */}
    </div>
  );
}

// Small helper used by all three enrichment cards. Renders a table of buckets
// exactly like the other analytics tables so the visual style matches.
function BucketCard({ eyebrow, title, subtitle, buckets, coverage, coverageLabel, fmpConfigured, header, testidPrefix }) {
  // If most/all trades land in the "unknown" bucket, FMP doesn't have data
  // for the user's tickers — usually because they trade micro-caps that
  // FMP's basic plan blocks (HTTP 402 responses). Show a helpful callout
  // instead of a wall of $0.00 rows.
  const totalTrades = (buckets || []).reduce((n, b) => n + (b?.trades || 0), 0);
  const unknownTrades = (buckets || []).find((b) => b?.bucket === "unknown")?.trades || 0;
  const mostlyUnknown = totalTrades > 0 && unknownTrades / totalTrades >= 0.8;

  return (
    <Card eyebrow={eyebrow} title={title}>
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-[11px] text-[color:var(--fg-3)] max-w-lg">{subtitle}</div>
        <div className="text-[10px] text-[color:var(--fg-3)] whitespace-nowrap ml-4">
          {typeof coverage === "number" ? `${coverage}% ${coverageLabel}` : ""}
          {fmpConfigured === false && " · admin: set FMP_API_KEY to enable"}
        </div>
      </div>
      {mostlyUnknown && fmpConfigured !== false && (
        <div
          className="mb-3 p-3 text-[11px]"
          style={{
            background: "rgba(251,191,36,0.08)",
            border: "1px solid rgba(251,191,36,0.30)",
            borderRadius: 6,
            color: "#fbbf24",
          }}
        >
          Most of your trades don&apos;t have data for this metric. Your ticker mix appears to be micro-cap
          symbols that aren&apos;t covered by our current data plan. Larger-cap tickers (AAPL, TSLA, SPY-listed)
          will show accurate {eyebrow.toLowerCase()} buckets. Nothing wrong with your trades — just a data-provider limitation.
        </div>
      )}
      {!buckets || buckets.length === 0 ? (
        <div className="text-xs text-[color:var(--fg-3)] p-3">No trades in this range yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono-num">
            <thead>
              <tr className="border-b border-[color:var(--border)] text-[color:var(--fg-3)] label-eyebrow">
                <th className="text-left p-3">{header}</th>
                <th className="text-right p-3">TRADES</th>
                <th className="text-right p-3">WIN RATE</th>
                <th className="text-right p-3">AVG P&L</th>
                <th className="text-right p-3">TOTAL P&L</th>
                <th className="text-left p-3">SAMPLE SYMBOLS</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr key={b.bucket} className="border-b border-[color:var(--border)] hover:bg-[color:var(--surface)]" data-testid={`${testidPrefix}-row-${b.bucket}`}>
                  <td className="p-3 text-[color:var(--fg)] font-semibold">{b.bucket}</td>
                  <td className="p-3 text-right text-[color:var(--fg-2)]">{b.trades}</td>
                  <td className="p-3 text-right text-[color:var(--fg-2)]">{b.win_rate}%</td>
                  <td className={`p-3 text-right ${b.avg_pnl >= 0 ? "text-[color:var(--green)]/90" : "text-[color:var(--loss)]/90"}`}>{fmtUSD(b.avg_pnl)}</td>
                  <td className={`p-3 text-right ${b.total_pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(b.total_pnl)}</td>
                  <td className="p-3 text-[10px] text-[color:var(--fg-3)]">{(b.symbols || []).slice(0, 6).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

